import { useMemo, useState } from "react";
import {
  ChevronDown,
  CalendarClock,
  AlertTriangle,
  Check,
  IndianRupee,
  Plus,
  Flag,
} from "lucide-react";
import {
  installmentDisplayState,
  type Installment,
  type InstallmentPlan,
  type InstallmentDisplayState,
} from "./types";
import { todayKey, daysBetween, toPaise, outstandingOf } from "./installmentMath";

/**
 * The EMI schedule as it appears on a deal card: a one-line summary that expands
 * into the full ladder.
 *
 * Collapsed, it answers the only two questions anyone asks at a glance — how far
 * through are we, and is anything late. Expanded, it is a statement: every row,
 * every date, and a footer that PROVES the rows still add up to the plan. A
 * finance table that doesn't show its own arithmetic is asking to be trusted
 * rather than earning it.
 */

function money(n: number): string {
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
}

/** Row treatment per state. Colours come from the app's existing palette. */
const ROW_STYLE: Record<
  InstallmentDisplayState,
  { border: string; bg: string; label: string; labelBg: string; labelFg: string }
> = {
  overdue:  { border: "#da2a46", bg: "#fff5f6", label: "Overdue",   labelBg: "#fdeaed", labelFg: "#da2a46" },
  dueToday: { border: "#ff8447", bg: "#fffbf5", label: "Due today", labelBg: "#fff4ec", labelFg: "#ff8447" },
  partial:  { border: "#e6b800", bg: "#fffdf3", label: "Part paid", labelBg: "#fff8dd", labelFg: "#8a6d00" },
  paid:     { border: "transparent", bg: "transparent", label: "Paid",   labelBg: "#e7f6ef", labelFg: "#156548" },
  waived:   { border: "transparent", bg: "transparent", label: "Waived", labelBg: "#eef1f0", labelFg: "#697571" },
  upcoming: { border: "transparent", bg: "transparent", label: "Upcoming", labelBg: "#f2f4f3", labelFg: "#697571" },
};

interface Props {
  plan: InstallmentPlan;
  /** The deal's own money — used to surface cash the ladder doesn't claim. */
  dealTotalPaid: number;
  dealNetAmount: number;
  /** Highlight + scroll to this row — set by the popup's "Record payment". */
  focusInstallmentId?: number | null;
  /** Opens the payment form with this instalment preselected. */
  onRecord?: (installment: Installment) => void;
  /** Applies unadjusted credit to the outstanding instalments, oldest first. */
  onApplyCredit?: () => void;
  applyingCredit?: boolean;
  /** Settle the whole remaining schedule at once. */
  onForeclose?: () => void;
  /** Record money that is NOT for a specific instalment — it lands unadjusted. */
  onAddUnadjusted?: () => void;
  /** Opens the plan editor — the way out when the schedule outgrew the deal. */
  onRebuild?: () => void;
  /** Reverses a foreclosure. */
  onReopen?: () => void;
  reopening?: boolean;
  /** Drops instalments that can never be collected because the deal is paid. */
  onCloseRemaining?: () => void;
  closingRemaining?: boolean;
}

export default function InstallmentSchedule({
  plan,
  dealTotalPaid,
  dealNetAmount,
  focusInstallmentId,
  onRecord,
  onApplyCredit,
  applyingCredit = false,
  onForeclose,
  onAddUnadjusted,
  onRebuild,
  onReopen,
  reopening = false,
  onCloseRemaining,
  closingRemaining = false,
}: Props) {
  // Open by default. The schedule IS the feature — collapsing it by default
  // made the card look like it had nothing in it, which is how the plan
  // appeared "not to save" at all. Collapsing stays available for a long
  // finished plan.
  const [open, setOpen] = useState(true);
  const today = todayKey();

  // The deal's own balance, in paise. Zero (or less) means nothing more can be
  // collected on it, whatever the schedule still lists.
  const dealSettled = toPaise(dealNetAmount) - toPaise(dealTotalPaid) <= 0;

  const rows = useMemo(
    () => [...plan.installments].sort((a, b) => a.seq - b.seq),
    [plan.installments],
  );

  /**
   * When the DEAL owes nothing, no instalment can owe anything either.
   *
   * A schedule built bigger than the deal leaves rows that say "₹1,666.64 left"
   * on a deal whose balance is zero. That figure is not collectable — every
   * payment path caps against the deal's pending — so showing it as outstanding
   * is the UI stating something untrue and then refusing every attempt to act on
   * it. Display them as settled.
   *
   * The stored rows still carry their original amounts; "Close the remaining
   * instalments" writes this truth down permanently by trimming them to the
   * money actually received. Until someone does that, the display simply stops
   * lying — it does not need a round-trip to be correct.
   */
  const states = useMemo(
    () =>
      new Map(
        rows.map((r) => [
          r.id,
          dealSettled ? ("paid" as const) : installmentDisplayState(r, today),
        ]),
      ),
    [rows, today, dealSettled],
  );

  const overdue = rows.filter((r) => states.get(r.id) === "overdue");
  const overdueAmount = overdue.reduce(
    (s, r) => s + outstandingOf(r),
    0,
  );

  /** The first row still owing anything — the one the counsellor chases next. */
  // Deal fully collected, yet the ladder still has open rows — the plan was
  // built bigger than the deal. Nothing here is collectable.
  const dealFullyPaid = dealSettled;
  const openCount = rows.filter(
    (r) => r.status !== "PAID" && r.status !== "WAIVED",
  ).length;

  // A closed plan with nothing outstanding — i.e. foreclosed or fully collected.
  const isForeclosed =
    plan.status === "COMPLETED" &&
    rows.length > 0 &&
    rows.every((r) => r.status === "PAID" || r.status === "WAIVED");

  const nextUnpaid = rows.find((r) => {
    const st = states.get(r.id);
    return st !== "paid" && st !== "waived";
  });

  const nextDue = rows.find((r) => {
    const st = states.get(r.id);
    return st !== "paid" && st !== "waived" && st !== "overdue";
  });

  // The reconciliation check, computed in paise so it is exact rather than
  // approximately right. If this ever fails the operator must be told, loudly —
  // silence on a ledger mismatch is the one thing a finance surface must not do.
  const scheduledPaise = rows.reduce((s, r) => s + toPaise(r.amount), 0);
  const balances = scheduledPaise === toPaise(plan.principal);

  // Money on the deal that NO instalment claims.
  //
  // Every deal that was collecting before its plan existed has some: those
  // payment rows carry no instalment link and can never acquire one. Without
  // this line the card reads "0 of 6 paid" beside a deal that has visibly
  // received money, which looks broken — it isn't, the cash simply isn't on the
  // ladder. Naming it is the difference between a puzzle and a fact.
  const unadjustedPaise = Math.max(
    0,
    toPaise(dealTotalPaid) - rows.reduce((s, r) => s + toPaise(r.paidAmount), 0),
  );
  const unadjusted = unadjustedPaise / 100;

  // What the whole schedule still owes, and whether unadjusted credit alone
  // already covers it. If it does, the next action is to CLOSE the plan with
  // that credit — not to ask for another instalment.
  const remainingBalancePaise = dealSettled
    ? 0
    : rows.reduce((sum, r) => sum + toPaise(outstandingOf(r)), 0);
  const remainingBalance = remainingBalancePaise / 100;
  const creditCoversBalance =
    remainingBalancePaise > 0 && unadjustedPaise >= remainingBalancePaise;

  // A plan can be built for more than the deal owes (the setup modal warns but
  // does not block). Keep saying so, rather than the warning existing only at
  // the moment of creation — the last instalment will hit the deal's pending
  // cap and be refused.
  const overFinancedPaise = Math.max(
    0,
    toPaise(plan.principal) - toPaise(dealNetAmount),
  );

  // The opposite gap: a schedule that collects LESS than the deal is worth. Not
  // an error — a part of the fee may be settled outside the plan — but it must
  // be visible, because otherwise the deal quietly never reaches fully-paid and
  // nobody can see which number is wrong.
  const underCoveredPaise = Math.max(
    0,
    toPaise(dealNetAmount) - toPaise(plan.principal) - unadjustedPaise,
  );

  // Reads off `states`, which already accounts for a settled deal — so the strip
  // can never say "3 of 6 paid" above a table showing six paid rows.
  const paidCount = rows.filter((r) => {
    const st = states.get(r.id);
    return st === "paid" || st === "waived";
  }).length;
  const paidAmount = rows.reduce((s, r) => s + r.paidAmount, 0);
  const countPct = rows.length ? Math.round((paidCount / rows.length) * 100) : 0;

  const accent = overdue.length ? "#da2a46" : nextDue && states.get(nextDue.id) === "dueToday" ? "#ff8447" : "#156548";

  return (
    <div className="px-4 pb-2">
      {/* ── Collapsed summary ─────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={(e) => {
          // The deal card's header toggles payment history on click; without
          // this the ladder and the history fight each other.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="w-full flex items-center gap-2 rounded-xl border border-[#e6e8e7] bg-white px-3 py-2 hover:bg-[#f7f9f8] transition-colors text-left"
      >
        <span
          className="size-1.5 rounded-full shrink-0"
          style={{ backgroundColor: accent }}
        />
        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[#697571] shrink-0">
          EMI
        </span>

        {overdue.length > 0 ? (
          <span className="text-[11px] font-medium text-[#da2a46] shrink-0 tabular-nums">
            {overdue.length} overdue · {money(overdueAmount)}
          </span>
        ) : null}

        <span className="text-[11px] text-[#374741] tabular-nums truncate">
          {paidCount} of {rows.length} paid ·{" "}
          {money(dealSettled ? dealTotalPaid : paidAmount)} of{" "}
          {money(dealSettled ? dealNetAmount : (plan.totalAmount ?? plan.principal))}
        </span>
        {unadjustedPaise > 0 && (
          <span className="text-[10px] font-medium text-[#335CFF] shrink-0 tabular-nums">
            · {money(unadjusted)} unadjusted
          </span>
        )}

        <span className="flex-1" />

        {dealSettled ? (
          <span className="text-[10px] font-medium text-[#156548] shrink-0">
            Fully paid
          </span>
        ) : (
          nextDue && (
            <span className="text-[10px] text-[#697571] shrink-0 tabular-nums hidden sm:inline">
              Next {money(outstandingOf(nextDue))} on {shortDate(nextDue.dueDate)}
            </span>
          )
        )}
        <ChevronDown
          className={`size-3.5 text-[#697571] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Progress by COUNT — deliberately different from the money bar above it,
          so the two can never be mistaken for each other. */}
      <div className="mt-1.5 h-[3px] rounded-full bg-[#eef1f0] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${countPct}%`, backgroundColor: accent }}
        />
      </div>

      {dealFullyPaid && nextUnpaid && (
        // THE STUCK STATE, and the only way out of it.
        //
        // The deal is fully paid but the schedule still lists instalments. They
        // can never be paid — the deal's pending balance is zero and every
        // payment path caps against it — so Pay, Foreclose and a normal re-plan
        // all refuse. The schedule was simply built too big. One button, one
        // sentence, done.
        <div className="mt-2 rounded-lg bg-[#f0faf5] border border-[#c1e1d5] px-2.5 py-2">
          <div className="flex items-start gap-1.5">
            <Check className="size-3.5 text-[#156548] mt-[1px] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-[#1a6b4c]">
                This deal is fully paid — {money(dealTotalPaid)} of{" "}
                {money(dealNetAmount)}
              </p>
              <p className="text-[10px] text-[#1a6b4c]/90 leading-[1.45] mt-0.5">
                The schedule still lists {openCount} instalment
                {openCount === 1 ? "" : "s"} that can never be collected, because
                the plan was made bigger than the deal. Nothing is owed. Close
                them and the plan is finished.
              </p>
              {onCloseRemaining && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseRemaining();
                  }}
                  disabled={closingRemaining}
                  className="mt-1.5 inline-flex items-center rounded-lg bg-[#218760] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-[#1a6b4c] disabled:opacity-50 transition-colors"
                >
                  {closingRemaining
                    ? "Closing…"
                    : `Close the ${openCount} remaining instalment${openCount === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isForeclosed && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-[#f7f9f8] border border-[#e6e8e7] px-2.5 py-2">
          <p className="text-[10px] text-[#697571] leading-[1.45]">
            <span className="font-semibold text-[#374741]">
              This plan is closed.
            </span>{" "}
            Every instalment was settled by a foreclosure, so these dates no
            longer remind anyone. Reopen it if that was a mistake.
          </p>
          {onReopen && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReopen();
              }}
              disabled={reopening}
              className="shrink-0 rounded-lg border border-[#e2e4e3] bg-white px-2.5 py-1 text-[10px] font-medium text-[#374741] hover:bg-[#f0f2f1] disabled:opacity-50"
            >
              {reopening ? "Reopening…" : "Reopen"}
            </button>
          )}
        </div>
      )}

      {/* The three things anyone actually does here, as buttons rather than text
          links buried in a table. "Add money" previously had NO route at all —
          the payment form demands an instalment once a plan exists — so cash
          that wasn't for a specific row simply could not be recorded. */}
      {nextUnpaid && !dealFullyPaid && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* When unadjusted credit already covers everything still owed, offering
              "Pay #4 · ₹1,666.64" is the wrong instruction — the customer owes
              nothing more, the money is just sitting unattached. Offer the thing
              that actually finishes it, with the real balance. */}
          {creditCoversBalance && onForeclose ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onForeclose();
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#218760] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-[#1a6b4c] transition-colors"
            >
              <Flag className="size-3" />
              Foreclose · {money(remainingBalance)} from credit
            </button>
          ) : (
            onRecord && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRecord(nextUnpaid);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#218760] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-[#1a6b4c] transition-colors"
              >
                <IndianRupee className="size-3" />
                Pay #{nextUnpaid.seq} · {money(outstandingOf(nextUnpaid))}
              </button>
            )
          )}
          {onAddUnadjusted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddUnadjusted();
              }}
              title="Record money that isn't for a specific instalment"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e4e3] bg-white px-3 py-1.5 text-[11px] font-medium text-[#374741] hover:bg-[#f7f9f8] transition-colors"
            >
              <Plus className="size-3" />
              Add money
            </button>
          )}
          {onForeclose && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onForeclose();
              }}
              title="Settle every remaining instalment at once"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e4e3] bg-white px-3 py-1.5 text-[11px] font-medium text-[#374741] hover:bg-[#f7f9f8] transition-colors"
            >
              <Flag className="size-3" />
              Foreclose
            </button>
          )}
        </div>
      )}

      {unadjustedPaise > 0 && (
        // Loud on purpose. This is money the customer has ALREADY paid that no
        // instalment is claiming — so the schedule will keep reporting those
        // instalments as due, and a counsellor will chase someone for money
        // already in the bank. That is the worst call anyone on this team can
        // make, so it does not get a calm blue info box.
        <div className="mt-2 rounded-lg bg-[#fff7ed] border border-[#fdba74] px-2.5 py-2">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="size-3.5 text-[#c2410c] mt-[1px] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-[#9a3412] tabular-nums">
                {money(unadjusted)} already paid, not on any instalment
              </p>
              <p className="text-[10px] text-[#9a3412]/85 leading-[1.45] mt-0.5">
                {rows.some((r) => r.paidAmount > 0)
                  ? "More was received than the schedule could take at the time."
                  : "Received before this plan existed, so it never attached to a row."}{" "}
                Until it is applied, these instalments keep showing as due and
                someone will chase money that is already in.
              </p>
              {onApplyCredit && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApplyCredit();
                  }}
                  disabled={applyingCredit || nextUnpaid === undefined}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-[#c2410c] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#9a3412] disabled:opacity-50 transition-colors"
                >
                  {applyingCredit
                    ? "Applying…"
                    : `Apply ${money(unadjusted)} to instalments`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suppressed while the deal is fully paid: the green banner above already
          says the schedule outgrew the deal AND carries the button that fixes
          it. Two warnings about one problem, one of which offers no action, is
          how a card starts reading as noise. */}
      {overFinancedPaise > 0 && !dealFullyPaid && (
        <div className="mt-2 rounded-lg bg-[#fffbf5] border border-[#ffe0c2] px-2.5 py-2">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="size-3.5 text-[#ff8447] mt-[1px] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-[#8a5a2b]">
                This schedule is {money(overFinancedPaise / 100)} bigger than the
                deal
              </p>
              <p className="text-[10px] text-[#8a5a2b]/90 leading-[1.45] mt-0.5">
                The plan collects {money(plan.principal)} but the deal is worth{" "}
                {money(dealNetAmount)}. Paying the later instalments will be
                refused, because a deal can never take more than it is worth.
                Change the plan so the remaining instalments add up to what
                the deal still owes.
              </p>
              {onRebuild && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRebuild();
                  }}
                  className="mt-1.5 inline-flex items-center rounded-lg bg-[#c2410c] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#9a3412] transition-colors"
                >
                  Change the plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {underCoveredPaise > 0 && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[#f7f9f8] border border-[#e6e8e7] px-2.5 py-1.5">
          <AlertTriangle className="size-3 text-[#697571] mt-[1px] shrink-0" />
          <p className="text-[10px] text-[#697571] leading-[1.45]">
            This schedule collects {money(plan.principal)} of the deal's{" "}
            {money(dealNetAmount)} —{" "}
            <span className="font-medium tabular-nums">
              {money(underCoveredPaise / 100)}
            </span>{" "}
            is not on any instalment. Fine if it is being settled outside the
            plan; otherwise the deal will never reach fully paid.
          </p>
        </div>
      )}

      {plan.outOfSyncAt && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[#fffbf5] border border-[#ffe0c2] px-2.5 py-1.5">
          <AlertTriangle className="size-3 text-[#ff8447] mt-[1px] shrink-0" />
          <p className="text-[10px] text-[#8a5a2b] leading-[1.45]">
            The deal's value changed after this plan was made, so the schedule no
            longer matches what is owed. Changing it only re-spreads the
            instalments nobody has paid yet — paid ones are never touched.
          </p>
        </div>
      )}

      {/* ── Expanded ladder ───────────────────────────────────────────────── */}
      {open && (
        <div className="mt-2 rounded-xl border border-[#e6e8e7] overflow-hidden">
          <div className="max-h-[320px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-[#f7f9f8] z-[1]">
                <tr className="text-[9px] uppercase tracking-[0.05em] text-[#697571]">
                  <th className="text-left font-medium px-2.5 py-1.5 w-8">#</th>
                  <th className="text-left font-medium px-2.5 py-1.5">Due</th>
                  <th className="text-right font-medium px-2.5 py-1.5">Amount</th>
                  <th className="text-right font-medium px-2.5 py-1.5">Paid</th>
                  <th className="text-left font-medium px-2.5 py-1.5">Status</th>
                  <th className="px-2.5 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = states.get(r.id) as InstallmentDisplayState;
                  const s = ROW_STYLE[st];
                  const outstanding = dealSettled ? 0 : outstandingOf(r);
                  const settled = st === "paid" || st === "waived";
                  const isFocus = focusInstallmentId === r.id;
                  const lateBy = st === "overdue" ? daysBetween(r.dueDate.slice(0, 10), today) : 0;
                  return (
                    <tr
                      key={r.id}
                      id={`inst-${r.id}`}
                      className={`border-t border-[#eef1f0] ${isFocus ? "ring-1 ring-inset ring-[#218760]" : ""}`}
                      style={{
                        backgroundColor: isFocus ? "#f2fbf7" : s.bg,
                        boxShadow: s.border !== "transparent" ? `inset 2px 0 0 ${s.border}` : undefined,
                      }}
                    >
                      <td className="px-2.5 py-1.5 text-[#9ba3a0] tabular-nums">{r.seq}</td>
                      <td className="px-2.5 py-1.5 text-[#374741] whitespace-nowrap">
                        {shortDate(r.dueDate)}
                        {lateBy > 0 && (
                          <span className="text-[9px] text-[#da2a46] ml-1">
                            · {lateBy}d late
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums text-[#1e302a]">
                        {money(r.amount)}
                      </td>
                      <td className={`px-2.5 py-1.5 text-right tabular-nums ${settled ? "text-[#8a918d]" : "text-[#374741]"}`}>
                        {r.paidAmount > 0 ? money(r.paidAmount) : "—"}
                        {/* On a part-paid row the useful number is what is
                            STILL owed, not what has landed — that is the figure
                            the counsellor is about to ask for. */}
                        {st === "partial" && (
                          <span className="block text-[9px] text-[#8a6d00]">
                            {money(outstanding)} left
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5">
                        <span
                          className="text-[9px] font-medium px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ backgroundColor: s.labelBg, color: s.labelFg }}
                        >
                          {st === "paid" && <Check className="size-2.5" />}
                          {s.label}
                        </span>
                      </td>
                      <td className="px-2.5 py-1.5 text-right">
                        {!settled && onRecord && (
                          <button
                            type="button"
                            onClick={() => onRecord(r)}
                            className="rounded-md border border-[#c1e1d5] bg-[#f0faf5] px-2 py-0.5 text-[10px] font-medium text-[#1a6b4c] hover:bg-[#e2f4ec] whitespace-nowrap"
                          >
                            Pay
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* The proof line. Always rendered, even when it balances — that is
              the point of it. */}
          <div
            className={`flex items-center justify-between px-2.5 py-2 border-t text-[10px] tabular-nums ${
              balances
                ? "border-[#e6e8e7] bg-[#f7f9f8] text-[#697571]"
                : "border-[#f3c9d0] bg-[#fff5f6] text-[#da2a46]"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <CalendarClock className="size-3" />
              {rows.length} instalments
            </span>
            <span>
              {unadjustedPaise > 0 && (
                <span className="text-[#335CFF] mr-2">
                  {money(unadjusted)} unadjusted
                </span>
              )}
              {money(scheduledPaise / 100)} scheduled ·{" "}
              {balances ? (
                <span className="text-[#156548] font-medium">matches plan total</span>
              ) : (
                <span className="font-medium">
                  does not match the plan total of {money(plan.principal)} — contact support
                </span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
